import React, { forwardRef, useContext } from 'react';
import classNames from 'classnames';
import { useMergeRefs, FloatingPortal, FloatingFocusManager, useTransitionStyles } from '@floating-ui/react';
import { mdiClose } from '@redsift/icons';

import { AppContainerContext, useMessageFormatter } from '@redsift/design-system';
import intlMessages from './intl';

import { Comp, IconButton, isComponent, partitionComponents, useTheme } from '@redsift/design-system';
import { DialogContentProps } from './types';
import { useDialogContext } from '../dialog';
import { StyledDialogContent, StyledFloatingOverlay } from './styles';
import { DialogContentActions } from '../dialog-content-actions';
import { DialogContentBody } from '../dialog-content-body';
import { DialogContentHeader } from '../dialog-content-header';

const COMPONENT_NAME = 'DialogContent';
const CLASSNAME = 'redsift-dialog-content';

/**
 * The DialogContent component.
 */
export const BaseDialogContent: Comp<DialogContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, style, theme: propsTheme, ...forwardedProps } = props;
  const appContainerState = useContext(AppContainerContext);
  const {
    context: floatingContext,
    getFloatingProps,
    refs,
    labelId,
    descriptionId,
    handleOpen,
    hasCloseButton,
    initialFocus: propsInitialFocus,
    width,
    minWidth,
    maxWidth,
    headerRef,
    bodyRef,
    actionsRef,
  } = useDialogContext();
  const theme = useTheme(propsTheme);
  const { isMounted, styles } = useTransitionStyles(floatingContext);
  const dialogRef = useMergeRefs([refs.setFloating, ref]);

  const format = useMessageFormatter(intlMessages);

  const [[header], [body], [actions]] = partitionComponents(React.Children.toArray(children), [
    isComponent('DialogContentHeader'),
    isComponent('DialogContentBody'),
    isComponent('DialogContentActions'),
  ]);

  const initialFocus: number | React.MutableRefObject<HTMLElement | null> | undefined =
    propsInitialFocus === 'header'
      ? headerRef
      : propsInitialFocus === 'body'
      ? bodyRef
      : propsInitialFocus === 'actions'
      ? actionsRef
      : propsInitialFocus
      ? propsInitialFocus
      : undefined;

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isMounted && (
        <StyledFloatingOverlay lockScroll style={styles}>
          <FloatingFocusManager context={floatingContext} initialFocus={initialFocus ? initialFocus : undefined}>
            <StyledDialogContent
              $theme={theme!}
              className={classNames(DialogContent.className, className)}
              {...forwardedProps}
              ref={dialogRef}
              aria-labelledby={labelId}
              aria-describedby={descriptionId}
              {...getFloatingProps(props)}
              style={{ ...styles, ...style }}
              $width={width}
              $minWidth={minWidth}
              $maxWidth={maxWidth}
            >
              {!header && !body && !actions ? (
                <DialogContentBody>{children}</DialogContentBody>
              ) : (
                <>
                  {header || hasCloseButton ? (
                    <div className={`${BaseDialogContent.className}__header`}>
                      {header}

                      {hasCloseButton ? (
                        <IconButton
                          theme={theme}
                          aria-label={format('close')}
                          className={`${BaseDialogContent.className}-header__icon-button`}
                          color="grey"
                          icon={mdiClose}
                          onClick={() => handleOpen(false)}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  {body || actions ? (
                    <div className={`${BaseDialogContent.className}__content`}>
                      {body}
                      {actions}
                    </div>
                  ) : null}
                </>
              )}
            </StyledDialogContent>
          </FloatingFocusManager>
        </StyledFloatingOverlay>
      )}
    </FloatingPortal>
  );
});
BaseDialogContent.className = CLASSNAME;
BaseDialogContent.displayName = COMPONENT_NAME;

export const DialogContent = Object.assign(BaseDialogContent, {
  Header: DialogContentHeader,
  Body: DialogContentBody,
  Actions: DialogContentActions,
});
